import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { setDocumentTitle } from "../hooks/useDocumentTitle";

/**
 * Titles for routes whose name is fixed. Longest match wins, so
 * `/rosters/roster-images` beats `/rosters`.
 *
 * Routes whose title depends on loaded data are deliberately absent. They call
 * `useDocumentTitle` themselves, and this component leaves the title alone for
 * any path it cannot match so it does not overwrite them. Effects in a parent
 * run after effects in its children, so a fallback here would always win.
 */
const STATIC_TITLES: Record<string, string> = {
  "/login": "Sign In",
  "/forgot-password": "Forgot Password",
  "/reset-password": "Reset Password",
  "/auth/complete-reset": "Account Email",
  "/accept-invite": "Accept Invitation",
  "/travel-ball/roster-invite": "Accept Invite",
  "/setup-program": "Set Up Your Program",
  "/signup": "Sign Up",
  "/signup/payment": "Complete Payment",
  "/signup/complete": "Finishing Signup",
  "/pricing": "Pricing",
  "/terms": "Terms of Service",
  "/privacy": "Privacy Policy",
  "/home": "Home",
  "/players": "Players",
  "/duplicates": "Merge Manager",
  "/trusted-contacts": "Trusted Contacts",
  "/calls": "Calls",
  "/schedule": "Schedule",
  "/tournaments": "Events",
  "/player-database": "Player Database",
  "/support": "Help Center",
  "/schedule/build-itinerary": "Build Itinerary",
  "/video": "Video",
  "/latest-notes": "Latest Notes",
  "/recruiting-notes/new": "New Recruiting Note",
  "/rosters": "Roster Planning",
  "/rosters/roster-images": "Roster Images",
  "/settings": "Settings",
  "/settings/users": "Users",
  "/settings/billing": "Billing",
  "/settings/branding": "Branding",
  "/admin": "Organizations",
  "/admin/notifications": "Notifications",
  "/admin/batch-extractions": "Batch Extractions",
  "/admin/batch-extractions/new": "New Batch Extraction",
  "/admin/roster-uploads": "Uploaded Rosters",
  "/admin/scraping": "Web Scraping",
  "/admin/messaging-numbers": "Messaging Numbers",
  "/admin/itinerary-requests": "Itinerary Requests",
  "/admin/sample-data": "Sample Data",
  "/admin/tours": "Tours",
  "/admin/feature-flags": "Feature Flags",
  "/admin/directory": "Directory Sources",
  "/admin/directory/health": "Directory Feed Health",
  "/admin/directory/runs": "Directory Runs",
  "/admin/directory/tournaments": "Directory Tournaments",
  "/admin/directory/review": "Directory Review",
  "/admin/directory/player-review": "Directory Player Review",
  "/admin/directory/players": "Directory Players",
  "/admin/directory/suppressions": "Directory Suppressions",
  "/admin/directory/exports": "Directory Rankings Export",
  "/admin/directory/rankings": "Directory Rankings",
  "/admin/directory/programs": "Directory Programs",
  "/admin/directory/sos": "Directory SOS",
  "/admin/directory/stat-lines": "Directory Stat Lines",
  "/admin/billing": "Billing",
  "/admin/billing/settings": "Billing Settings",
  "/admin/self-serve": "Self-serve Signup",
  "/admin/self-serve/tiers": "Self-serve Pricing Tiers",
  "/admin/self-serve/email-rules": "Self-serve Email Rules",
  // /crm/contacts is absent on purpose: its title (Players, Coaches or Parents) depends on the
  // ?type= query, so ContactListPage titles itself.
  "/crm/import": "Import Schools & Coaches",
  "/crm/communications": "Messages",
  "/crm/messages/unmatched": "Unmatched Messages",
  "/crm/twitter/followers": "X Followers",
  "/crm/submissions/pending": "Pending Submissions",
  "/crm/teams": "Schools",
  "/crm/connectors": "Connectors",
  "/crm/settings/x-post-templates": "X Post Templates",
  "/messaging": "Messaging",
  "/messaging/templates": "Templates",
  // /messaging/numbers is absent on purpose: the tab is "Program line" or "Numbers" by flag, so
  // each version of the page titles itself.
};

const titleForPath = (pathname: string): string | null => {
  const normalized =
    pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  return STATIC_TITLES[normalized] ?? null;
};

export const RouteTitle = () => {
  const { pathname } = useLocation();
  const title = titleForPath(pathname);

  useEffect(() => {
    if (title) setDocumentTitle(title);
  }, [title]);

  return null;
};
